// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@flip/App/Store/Reducers";
import { getSettingsCollection } from "@flip/App/Store/Selectors/SettingsSelectors";
import { XNGAGE_CUSTOM_WIDGET } from "@flip/XDL/constants";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    currentLocation: getLocation(state),
    zoomInfoScript: getSettingsCollection(state).customSettings.zoomInfoScript,
    isCookieBannerAccepted: state.xCommon.isCookieBannerAccepted,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const ZoomInfo: React.FC<Props> = ({ currentLocation, zoomInfoScript, isCookieBannerAccepted }) => {
    const scriptRef = React.useRef<HTMLScriptElement | null>(null);

    const injectZoomInfoScript = (zoomInfoScript: string) => {
        if (scriptRef.current && document.head.contains(scriptRef.current)) {
            document.head.removeChild(scriptRef.current);
            scriptRef.current = null;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(zoomInfoScript, "text/html");
        const scriptTag = doc.querySelector("script");

        if (scriptTag) {
            const script = document.createElement("script");

            if (scriptTag.src) {
                script.src = scriptTag.src;
                script.async = true;
            } else {
                script.textContent = scriptTag.textContent;
            }

            document.head.appendChild(script);
            scriptRef.current = script;
        }
    };

    React.useEffect(() => {
        if (!zoomInfoScript || !isCookieBannerAccepted) {
            return;
        }

        injectZoomInfoScript(zoomInfoScript);
    }, [currentLocation, zoomInfoScript, isCookieBannerAccepted]);

    return null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ZoomInfo),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [],
    },
};

export default widgetModule;
